import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

const API_URL = environment.BASE_API_URL + 'GGKCourseWebService.asmx';
const GGK_API_KEY = environment.GGK_API_KEY;

// --- Response Interfaces ---

export interface HoleData {
  hole: number;
  par: number;
  yards: number;
  hdcp: number;
}

export interface TeeSnapshot {
  tees_id: string;
  teename: string;
  rating: string;
  slope: number;
  par: number;
  total_yards: number;
  holes: HoleData[];
}

export interface TeeChange {
  type: 'insert' | 'update';
  tee_name: string;
  before: TeeSnapshot | null;
  after: TeeSnapshot;
  sql: string;
}

export interface ActiveEventWarning {
  event_name: string;
  event_date: string;
  tees_id: string;
}

export interface ValidationError {
  tee_name: string;
  rule: string;
  message: string;
}

export interface UploadAgentResponse {
  success: boolean;
  session_id?: string;
  tee_changes?: TeeChange[];
  warnings?: ActiveEventWarning[];
  skipped_tees?: string[];
  skipped_tees_details?: SkippedTeeDetail[];
  error?: string;
  validation_errors?: ValidationError[];
  async_processing?: boolean;
}

export interface SkippedTeeDetail {
  tee_name: string;
  tees_id: string;
  rating: string;
  slope: number;
  par: number;
  total_yards: number;
  holes: HoleData[];
}

export interface ExecuteSqlResponse {
  success: boolean;
  error?: string;
}

export interface ScorecardPreviewDialogData {
  session_id: string;
  warnings: ActiveEventWarning[];
  tee_changes: TeeChange[];
}

// --- Service ---

@Injectable({ providedIn: 'root' })
export class ScorecardAgentService {

  constructor(private http: HttpClient) { }

  private post<T>(url: string, payload: any): Observable<T> {
    return this.http.post<any>(url, payload, { params: { 'api_key': GGK_API_KEY } }).pipe(
      map((resp: any) => {
        // ASMX wraps responses in { d: "{...}" } - unwrap and parse
        const parsed = resp.d
          ? (typeof resp.d === 'string' ? JSON.parse(resp.d) : resp.d)
          : resp;
        return parsed as T;
      })
    );
  }

  uploadScorecard(course_id: string, image_base64: string, skip_course_check: boolean = false): Observable<UploadAgentResponse> {
    const payload = { course_id, image_base64, skip_course_check: skip_course_check ? 'true' : 'false' };
    const url = API_URL + '/uploadScorecardAgent';
    return this.post<UploadAgentResponse>(url, payload);
  }

  getScorecardResult(session_id: string): Observable<UploadAgentResponse> {
    const payload = { session_id };
    const url = API_URL + '/getScorecardResult';
    return this.post<UploadAgentResponse>(url, payload);
  }

  rateScorecardAgent(session_id: string, user_action: string, rating: number | null, notes: string, tees_applied: number, tees_skipped_by_user: number): Observable<any> {
    const payload = {
      session_id,
      user_action,
      rating: rating !== null ? rating.toString() : '',
      notes: notes || '',
      tees_applied: tees_applied.toString(),
      tees_skipped_by_user: tees_skipped_by_user.toString()
    };
    const url = API_URL + '/rateScorecardAgent';
    return this.post<any>(url, payload);
  }

  getProcessStatus(session_id: string): Observable<{ status: string; running: number }> {
    const payload = { session_id };
    const url = environment.BASE_API_URL + 'GGKEventWebService.asmx/getProcessStatus';
    return this.post<{ status: string; running: number }>(url, payload);
  }

  executeSql(session_id: string, sql_statements: string[]): Observable<ExecuteSqlResponse> {
    const payload = { session_id, sql_statements_json: JSON.stringify(sql_statements) };
    const url = API_URL + '/executeScorecardSql';
    return this.post<ExecuteSqlResponse>(url, payload);
  }
}
